<template>
    <ul
        class="ai-summary-list"
        :class="`ai-summary-list--${variant}`"
    >
        <li
            v-for="(item, index) in normalizedItems"
            :key="index"
            class="ai-summary-list__item"
        >
            <span class="ai-summary-list__label">
                <MarkdownView
                    :value="item.label"
                    inline
                />
            </span>
            <span
                v-if="variant === 'numbered' && item.value != null && item.value !== ''"
                class="ai-summary-list__badge"
            >
                {{ item.value }}
            </span>
        </li>
    </ul>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import MarkdownView from '@/components/RedesignBaseComponents/MarkdownView.vue'

export interface AiSummaryListItem {
    label: string
    value?: string | number
}

const props = defineProps<{
    variant: 'numbered' | 'bullets'
    items: AiSummaryListItem[] | string[]
}>()

const normalizedItems = computed<AiSummaryListItem[]>(() =>
    props.items.map(item => typeof item === 'string' ? { label: item } : item)
)
</script>

<style lang="scss" scoped>
.ai-summary-list {
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 14px;

    &--numbered {
        counter-reset: ai-summary-list;
    }

    &__item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 0;
        border-bottom: 1px solid var(--field-borders);

        &:last-child {
            border-bottom: none;
        }
    }

    &__label {
        flex: 1;
        min-width: 0;
        line-height: 1.4;
        word-break: break-word;
    }

    &--numbered &__label::before {
        counter-increment: ai-summary-list;
        content: counter(ai-summary-list) '. ';
        color: var(--gray-700);
    }

    &--bullets &__label::before {
        content: '● ';
        color: var(--gray-700);
    }

    &__badge {
        flex-shrink: 0;
        min-width: 28px;
        padding: 2px 10px;
        border-radius: 999px;
        background-color: var(--gray-200);
        font-weight: 600;
        text-align: center;
        font-variant-numeric: tabular-nums;
    }
}
</style>
